import { Box, chakra, HStack, Separator, Text, VStack } from '@chakra-ui/react';
import React from 'react';

import config from 'configs/app';
import { useColorModeValue } from 'toolkit/chakra/color-mode';
import { Image } from 'toolkit/chakra/image';
import { Link } from 'toolkit/chakra/link';
import IconSvg from 'ui/shared/IconSvg';
import { CONTENT_MAX_WIDTH } from 'ui/shared/layout/utils';
import NetworkAddToWallet from 'ui/shared/NetworkAddToWallet';

import { FOOTER_QUICK_LINKS, FooterQuickLinkIcon, type FooterQuickLinkIconId } from './quickLinks';

/**
 * Blockscout brand mark used in footer.
 */
type BlockscoutMarkProps = Omit<React.ComponentProps<typeof IconSvg>, 'name'>;

const BlockscoutMark = (props: BlockscoutMarkProps) => {
  return (
    <IconSvg
      boxSize={ 5 }
      flexShrink={ 0 }
      aria-hidden="true"
      { ...props }
      name="networks/icon-placeholder"
    />
  );
};

/**
 * A small icon+text link used on the right side of BBVision footer.
 * Keeps consistent spacing/hover styles and avoids repeating markup.
 */
const FooterQuickLink = ({ icon, label, href }: { icon: FooterQuickLinkIconId; label: string; href: string }) => {
  return (
    <Link
      href={ href }
      external
      noIcon
      display="inline-flex"
      alignItems="center"
      columnGap={ 2 }
      textStyle="sm"
      color="text.secondary"
      _hover={{ color: 'text.primary' }}
    >
      <FooterQuickLinkIcon icon={ icon }/>
      <chakra.span display={{ base: 'none', lg: 'inline' }}>{ label }</chakra.span>
    </Link>
  );
};

/**
 * BBVision footer (matches Figma / provided screenshot).
 *
 * Notes:
 * - This repo currently ships BBVision theme tokens, so we render BBVision footer directly.
 * - If we ever need to support multiple brands in one build, we can split this into
 *   `BBVisionFooter.tsx` + `BlockscoutFooter.tsx` and switch by config/env.
 */
const Footer = () => {
  const year = (new Date()).getFullYear();
  const logoSrc = useColorModeValue('/static/bbscan-logo.svg', '/static/bbscan-logo-dark.svg');

  const builtByLink = (
    <Link
      href="https://blockscout.com/"
      external
      noIcon
      display="inline-flex"
      alignItems="center"
      columnGap={ 2 }
      textStyle="sm"
      role="group"
      textDecoration="none"
    >
      { /* Both text parts hover together (as one link). */ }
      <chakra.span
        color="text.secondary"
        _groupHover={{ color: 'text.accent', textDecoration: 'underline' }}
      >
        Built by
      </chakra.span>
      <BlockscoutMark color="text.accent"/>
      <chakra.span
        color="text.primary"
        fontWeight={ 600 }
        _groupHover={{ color: 'text.accent', textDecoration: 'underline' }}
      >
        Blockscout
      </chakra.span>
    </Link>
  );

  return (
    <Box
      as="footer"
      borderTopWidth="1px"
      borderTopColor="border.default"
      px={{ base: 4, lg: config.UI.navigation.layout === 'horizontal' ? 6 : 12, '2xl': 6 }}
      py={{ base: 4, lg: 6 }}
    >
      <VStack
        maxW={ `${ CONTENT_MAX_WIDTH }px` }
        m="0 auto"
        alignItems="stretch"
        gap={{ base: 3, lg: 4 }}
      >
        { /* Row 1: logo (left) + quick links (right) */ }
        <HStack justifyContent="space-between" alignItems="center" w="full">
          <Link href="/" noIcon display="inline-flex" alignItems="center">
            <Image
              src={ logoSrc }
              alt="BBVision"
              height="24px"
              width="auto"
              objectFit="contain"
            />
          </Link>

          <HStack gap={{ base: 4, lg: 6 }} alignItems="center">
            { FOOTER_QUICK_LINKS.map((item) => (
              <FooterQuickLink
                key={ item.href }
                icon={ item.icon }
                label={ item.label }
                href={ item.href }
              />
            )) }
          </HStack>
        </HStack>

        { /* Row 2 (mobile): column layout */ }
        <VStack
          display={{ base: 'flex', lg: 'none' }}
          alignItems="flex-start"
          gap={ 2 }
          color="text.secondary"
          textStyle="sm"
          w="full"
        >
          <NetworkAddToWallet source="Footer"/>
          { builtByLink }
          <Text>
            BBScan © { year } All rights reserved
          </Text>
        </VStack>

        { /* Row 2 (desktop): single row with separators */ }
        <HStack
          display={{ base: 'none', lg: 'flex' }}
          alignItems="center"
          gap={ 3 }
          color="text.secondary"
          textStyle="sm"
          separator={ <Separator orientation="vertical" height={ 4 }/> }
        >
          <Text>
            BBScan © { year } All rights reserved
          </Text>
          { builtByLink }
          <NetworkAddToWallet source="Footer"/>
        </HStack>
      </VStack>
    </Box>
  );
};

export default React.memo(Footer);
